import { Bell, X, ShoppingCart, ClipboardCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useNotificationCount, useMarkNotificationsAsRead } from "@/lib/api/hooks/useNotification";
import { authService } from "@/lib/api/services/auth.service";

export function NotificationPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, right: 0 });
  const navigate = useNavigate();

  // Get firmId from user
  const user = authService.getCurrentUser();
  const firmId = user?.firmId;

  // Fetch notification count from API
  const { data: notificationCount, isLoading } = useNotificationCount(firmId);
  const markAsRead = useMarkNotificationsAsRead();
  
  // Get separate counts
  const ordersCount = notificationCount?.ordersCount || 0;
  const receiptsCount = notificationCount?.receiptsCount || 0;
  const totalCount = ordersCount + receiptsCount;

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const updatePosition = () => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          setPopupPosition({
            top: rect.bottom + 8,
            right: window.innerWidth - rect.right,
          });
        }
      };

      updatePosition();

      // Update position on scroll and resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen]);

  return (
    <div className="relative">
      <Button
        ref={buttonRef}
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Bell className="h-5 w-5" />
        {totalCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-white text-[10px] font-semibold flex items-center justify-center animate-pulse">
            {totalCount > 99 ? '99+' : totalCount}
          </span>
        )}
      </Button>

      {isOpen &&
        createPortal(
        <>
          <div 
              className="fixed inset-0 z-[9998]" 
            onClick={() => setIsOpen(false)}
          />
            <Card 
              className="fixed w-80 z-[9999] shadow-2xl border-border/50 max-h-96 overflow-y-auto bg-background"
              style={{
                top: `${popupPosition.top}px`,
                right: `${popupPosition.right}px`,
              }}
            >
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-primary" />
                <h3 className="font-semibold">Notifications</h3>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  Loading...
                </div>
              ) : totalCount === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  No new notifications
                </div>
              ) : (
                <div className="divide-y">
                  {ordersCount > 0 && (
                    <div 
                      className="p-4 hover:bg-accent/50 transition-colors cursor-pointer"
                      onClick={async () => {
                        setIsOpen(false);
                        // Mark orders notifications as read (mode 1)
                        await markAsRead(firmId, 1);
                        navigate("/orders");
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-full bg-primary/10 text-primary">
                          <ShoppingCart className="h-4 w-4" />
                        </div>
                        <div className="flex-1 space-y-1">
                          <p className="text-sm font-medium leading-none">
                            New Orders
                          </p>
                          <p className="text-xs text-muted-foreground">
                            You have {ordersCount} new order{ordersCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-semibold">
                          {ordersCount}
                        </span>
                      </div>
                    </div>
                  )}
                  {receiptsCount > 0 && (
                    <div 
                      className="p-4 hover:bg-accent/50 transition-colors cursor-pointer"
                      onClick={async () => {
                        setIsOpen(false);
                        // Mark receipts notifications as read (mode 2)
                        await markAsRead(firmId, 2);
                        navigate("/receipts");
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-full bg-accent/20 text-accent">
                          <ClipboardCheck className="h-4 w-4" />
                        </div>
                        <div className="flex-1 space-y-1">
                          <p className="text-sm font-medium leading-none">
                            New Receipts
                          </p>
                          <p className="text-xs text-muted-foreground">
                            You have {receiptsCount} new receipt{receiptsCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <span className="text-xs bg-accent/20 text-accent px-2 py-0.5 rounded-full font-semibold">
                          {receiptsCount}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          </>,
          document.body
      )}
    </div>
  );
}
